/** Периоды «Аналитики Ozon»: пресеты от сегодняшней даты и период сравнения.
 *  Все даты — строки YYYY-MM-DD, арифметика календарная (без часовых поясов). */

export type PresetId = "y" | "tw" | "pw" | "tm" | "pm" | "tq" | "pq" | "ytd" | "custom";
export type CmpMode = "off" | "prev" | "yoy" | "custom";

export interface Range {
  from: string;
  to: string;
}

export const PRESETS: { id: PresetId; label: string }[] = [
  { id: "y", label: "Вчера" },
  { id: "tw", label: "Эта неделя" },
  { id: "pw", label: "Прошлая неделя" },
  { id: "tm", label: "Этот месяц" },
  { id: "pm", label: "Прошлый месяц" },
  { id: "tq", label: "Этот квартал" },
  { id: "pq", label: "Прошлый квартал" },
  { id: "ytd", label: "С начала года" },
  { id: "custom", label: "Свой" },
];

export const CMP_MODES: { id: CmpMode; label: string }[] = [
  { id: "off", label: "Без сравнения" },
  { id: "prev", label: "Предыдущий такой же период" },
  { id: "yoy", label: "Тот же период год назад" },
  { id: "custom", label: "Свой период" },
];

interface Ymd {
  y: number;
  m: number; // 1…12
  d: number;
}

const pad = (n: number): string => String(n).padStart(2, "0");

export function isIsoDate(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const { y, m, d } = parse(s);
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
}

function parse(s: string): Ymd {
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return { y, m, d };
}

function fmt({ y, m, d }: Ymd): string {
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Сегодня (локальная дата) → YYYY-MM-DD. */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(s: string, days: number): string {
  const { y, m, d } = parse(s);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return fmt({ y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() });
}

/** Число дней в периоде, обе границы включительно. */
export function rangeDays(r: Range): number {
  const a = parse(r.from);
  const b = parse(r.to);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000) + 1;
}

const monthIndex = ({ y, m }: Ymd): number => y * 12 + (m - 1);
const fromIndex = (i: number): { y: number; m: number } => ({ y: Math.floor(i / 12), m: (i % 12) + 1 });

function isMonthEnd(s: string): boolean {
  const { y, m, d } = parse(s);
  return d === daysInMonth(y, m);
}

/** Сдвиг даты на k месяцев: число месяца прижимается к концу месяца (31.03 − 1 мес → 28/29.02);
 *  `toEnd` — результат всегда последний день месяца (для правой границы целых месяцев). */
export function shiftMonths(s: string, k: number, toEnd = false): string {
  const p = parse(s);
  const { y, m } = fromIndex(monthIndex(p) + k);
  const last = daysInMonth(y, m);
  return fmt({ y, m, d: toEnd ? last : Math.min(p.d, last) });
}

function monthStart(s: string): string {
  const { y, m } = parse(s);
  return fmt({ y, m, d: 1 });
}

/** Понедельник недели даты (недели — с понедельника). */
function weekStart(s: string): string {
  const { y, m, d } = parse(s);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 — воскресенье
  return addDays(s, -((dow + 6) % 7));
}

function quarterStart(s: string): string {
  const { y, m } = parse(s);
  return fmt({ y, m: Math.floor((m - 1) / 3) * 3 + 1, d: 1 });
}

/** Диапазон пресета на дату `today`. «Вчера» — один вчерашний день. «Этот …» и «с начала года» — по
 *  вчера включительно: начисления выгружаются утром за вчера, за сегодня данных нет. В первый день
 *  недели/месяца/квартала/года вчера раньше начала — период из одного сегодняшнего дня (данных нет —
 *  предупредит плашка «период длиннее витрины»). Недели — с понедельника по воскресенье. */
export function presetRange(id: Exclude<PresetId, "custom">, today: string): Range {
  const upTo = (from: string): Range => {
    const y = addDays(today, -1);
    return { from, to: y < from ? today : y };
  };
  switch (id) {
    case "y": {
      const y = addDays(today, -1);
      return { from: y, to: y };
    }
    case "tw":
      return upTo(weekStart(today));
    case "pw": {
      const from = addDays(weekStart(today), -7);
      return { from, to: addDays(from, 6) };
    }
    case "pm": {
      const from = shiftMonths(monthStart(today), -1);
      return { from, to: shiftMonths(from, 0, true) };
    }
    case "tm":
      return upTo(monthStart(today));
    case "pq": {
      const from = shiftMonths(quarterStart(today), -3);
      return { from, to: shiftMonths(from, 2, true) };
    }
    case "tq":
      return upTo(quarterStart(today));
    case "ytd": {
      const { y } = parse(today);
      return upTo(`${y}-01-01`);
    }
  }
}

/** Шаг «предыдущего такого же периода» в месяцах: месяц/квартал/год; null — по дням. */
function prevStepMonths(r: Range, preset: PresetId): number | null {
  if (preset === "tm") return 1;
  if (preset === "tq") return 3;
  if (preset === "ytd") return 12;
  // целые календарные месяцы (прошлый месяц/квартал или свой такой же) — сдвиг на их число
  if (parse(r.from).d === 1 && isMonthEnd(r.to)) {
    return monthIndex(parse(r.to)) - monthIndex(parse(r.from)) + 1;
  }
  return null;
}

function shiftRange(r: Range, k: number): Range {
  return { from: shiftMonths(r.from, k), to: shiftMonths(r.to, k, isMonthEnd(r.to)) };
}

/** Обрезка периода концом данных витрины: «этот месяц» 1–31 при данных до 15-го → 1–15.
 *  Период, целиком лежащий за концом данных, не трогаем (обрезать нечем — предупредит плашка). */
export function clampToData(r: Range, dataTo: string | null | undefined): Range {
  if (!dataTo || r.to <= dataTo || r.from > dataTo) return r;
  return { from: r.from, to: dataTo };
}

/** Период сравнения для режима `prev`/`yoy` (для `off`/`custom` — null, их берут из адреса).
 *
 *  prev: целые месяцы → столько же месяцев перед ними (сентябрь → август, III кв. → II кв.);
 *  «этот месяц/квартал» → те же дни месяц/квартал назад (1–16 окт → 1–16 сен);
 *  «с начала года» → те же даты год назад; «эта/прошлая неделя» → те же дни неделей раньше (вт 20.10:
 *  неделя 19–19.10 → 12–12.10, не 18.10); «вчера» и произвольный период → столько же дней вплотную перед.
 *  yoy: те же даты год назад (конец месяца остаётся концом месяца: 29.02 → 28.02).
 *
 *  `dataTo` — конец данных витрины: шаг (месяцы/дни) определяется по периоду как выбран, а сдвигается
 *  обрезанный по данным период — «этот месяц» 1–15 сравнивается с 1–15 прошлого, а не с целым. */
export function comparisonRange(
  mode: CmpMode,
  r: Range,
  preset: PresetId,
  dataTo?: string | null,
): Range | null {
  if (mode !== "prev" && mode !== "yoy") return null;
  const c = clampToData(r, dataTo);
  if (mode === "yoy") return shiftRange(c, -12);
  if (preset === "tw" || preset === "pw") return { from: addDays(c.from, -7), to: addDays(c.to, -7) };
  const step = prevStepMonths(r, preset);
  if (step !== null) return shiftRange(c, -step);
  const days = rangeDays(c);
  const to = addDays(c.from, -1);
  return { from: addDays(to, -(days - 1)), to };
}

/** Месяцы YYYY-MM, которые задевает период (для сверки). */
export function monthsOf(r: Range): string[] {
  const a = monthIndex(parse(r.from));
  const b = monthIndex(parse(r.to));
  const out: string[] = [];
  for (let i = a; i <= b; i++) {
    const { y, m } = fromIndex(i);
    out.push(`${y}-${pad(m)}`);
  }
  return out;
}
